import { useCallback, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import { tablePaginationClasses } from '@mui/material/TablePagination'
import MaskedComponent, { UseVinUnmaskReturn } from '../grid/Knock/MaskedComponent'
import {
  DataGridPro,
  type DataGridProProps,
  type GridColDef,
  type GridColumnOrderChangeParams,
  type GridColumnResizeParams,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridPaginationModel,
  type GridPinnedColumns,
  type GridRowsProp,
  type GridSortModel,
  type GridValidRowModel,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { useQuery } from '@tanstack/react-query'
import getOr from 'lodash/fp/getOr'

import { useFeature } from '@cbgms/base/feature-flags'

import { useAutoHeight } from './AutoHeightProvider'
import { DataGridHeader } from './DataGridHeader'
import { DataGridToolbar, DataGridToolbarAddButton } from './DataGridToolbar'
import { mapStructureToColumnTypeLookupTable, queryColumnDefinitionByColumnField } from './data-grid-column-definitions'
import {
  combineColumnVisibilityModelAndStructure,
  createAllColumnsVisibleColumnVisibilityModel,
  mapListColumnSettingsToColumnVisibilityModel,
  mapVisibilityModelToListColumns,
  shouldCreateAllColumnsVisibleColumnVisibilityModel,
  sortColumnVisibilityModelByColumnOrder
} from './data-grid-column-visibility'
import { type ColumnWidths, mapColumnsToColumnWidths, mapListSettingsToColumnWidths, queryColumnWidth } from './data-grid-column-width'
import { type GridFilterOperators, mapGridFilterModelToListSearch } from './data-grid-filtering'
import { mapListSettingsToPinnedColumns } from './data-grid-pinned-columns'
import { useSyncListSettings } from './data-grid-settings'
import { mapListSettingsToSortModel, mapSortModelToListOrderBy } from './data-grid-sorting'
import { useDataGridLocaleText } from './use-data-grid-locale-text'
import { type ExportData, type LoadData, type LoadSettings, type RestoreDefaultSettings, type SaveSettings } from './use-list-service/types'
import { useListTranslation } from './use-list-translation'
import { findKeyInObject } from '@cbgms/base/utils/helpers'

/**
 * This type maps a nested object (usually returned as the structure from a list call)
 * to a dot separated string union of all the properties.
 * @example
 * ```
 * interface NestedStructure {
 *   Object1: {
 *     Property1: string
 *   }
 *   Object2: {
 *     Property2: number
 *     Property3: boolean
 *   }
 * }
 *
 * // Results in a type of "Object1.Property1" | "Object2.Property2" | "Object2.Property3".
 * type MyColumnFields = ColumnField<NestedStructure>
 * ```
 */
type ColumnFields<T> = `${keyof { [P in keyof T as `${string & P}.${keyof T[P] & string}`]: never } & string}`

interface GridFilterModelItem<T extends GridValidRowModel> {
  field: ColumnFields<T>
  value?: any
  operator: GridFilterOperators
}

interface GridState<T extends GridValidRowModel> {
  settingsInitialized: boolean
  columns: GridColDef<T>[]
  columnVisibilityModel: GridColumnVisibilityModel
  columnWidths: ColumnWidths
  filterModel: GridFilterModel
  paginationModel: GridPaginationModel
  pinnedColumns: GridPinnedColumns
  sortModel: GridSortModel
  rowCount: number
  rows: GridRowsProp<T>
  hasAllColumnsMadeVisible?: boolean
}

const defaultState: GridState<any> = {
  settingsInitialized: false,
  columns: [],
  columnVisibilityModel: {},
  columnWidths: {},
  filterModel: {
    items: []
  },
  paginationModel: {
    page: 0,
    pageSize: 25
  },
  pinnedColumns: {
    left: [],
    right: []
  },
  sortModel: [],
  rowCount: 0,
  rows: [],
  hasAllColumnsMadeVisible: false
}

declare module '@mui/x-data-grid-pro' {
  interface GridApiPro {
    restoreSettings: () => Promise<void>
  }
}

interface UseDataGridPropsConfig<T extends GridValidRowModel> {
  exportCSV: ExportData
  exportExcel: ExportData
  getRowId: DataGridProProps<T>['getRowId']
  initialFilterModel?: GridFilterModelItem<T>[]
  key: string
  loadData: LoadData<T>
  loadSettings: LoadSettings
  saveSettings: SaveSettings
  restoreSettings?: RestoreDefaultSettings
  useVinUnmask?: (params: { vin: string; vehicleUUID?: string; workorderUUID?: string; resourceID?: string }) => UseVinUnmaskReturn
}

export function useDataGridProps<T extends GridValidRowModel>({
  exportCSV,
  exportExcel,
  getRowId,
  initialFilterModel = [],
  key,
  loadData,
  loadSettings,
  restoreSettings,
  saveSettings,
  useVinUnmask
}: UseDataGridPropsConfig<T>): DataGridProProps<T> {
  const { tl } = useListTranslation()
  const isSupplierOrderEnabled = useFeature('fr0079_enable_supplier_order')

  const [state, setState] = useState<GridState<T>>(() => ({
    ...defaultState,
    filterModel: {
      ...defaultState.filterModel,
      items: [
        ...defaultState.filterModel.items,
        ...initialFilterModel.map((item, index) => ({
          ...item,
          // The id property is required when there are multiple filters.
          // The user of the code shouldn't really be aware of needing to add the id.
          // That's why we're adding it here based on the index since it's only
          // set at the initialization.
          id: index
        }))
      ]
    }
  }))

  function onColumnVisibilityModelChange(columnVisibilityModel: GridColumnVisibilityModel) {
    setState(prevState => {
      // MUI's columnVisibilityModel is actually completely empty when we want to show all
      // of the columns. This doesn't match our implementation all too well since we
      // expect to receive keys for all of the visibile columns. Because of that we need
      // to check if we need to create a columnVisibilityModel that works for us.
      if (shouldCreateAllColumnsVisibleColumnVisibilityModel(columnVisibilityModel)) {
        return {
          ...prevState,
          columnVisibilityModel: createAllColumnsVisibleColumnVisibilityModel(prevState.columns),
          hasAllColumnsMadeVisible: true
        }
      }
      return {
        ...prevState,
        columnVisibilityModel,
        hasAllColumnsMadeVisible: false
      }
    })
  }

  const onFilterModelChange = useCallback((filterModel: GridFilterModel) => {
    setState(prevState => {
      return {
        ...prevState,
        filterModel,
        paginationModel: {
          ...prevState.paginationModel,
          // reset to page 0 because filtering could result in only a single page result
          page: 0
        }
      }
    })
  }, [])

  const onPaginationModelChange = useCallback((paginationModel: GridPaginationModel) => {
    setState(prevState => {
      const page = prevState.paginationModel.pageSize !== paginationModel.pageSize ? 0 : paginationModel.page
      return {
        ...prevState,
        paginationModel: {
          ...paginationModel,
          page
        }
      }
    })
  }, [])

  const onPinnedColumnsChange = useCallback((pinnedColumns: GridPinnedColumns) => {
    setState(prevState => {
      return {
        ...prevState,
        pinnedColumns
      }
    })
  }, [])

  const onSortModelChange = useCallback((sortModel: GridSortModel) => {
    setState(prevState => {
      return {
        ...prevState,
        sortModel
      }
    })
  }, [])

  const onColumnWidthChange = useCallback((params: GridColumnResizeParams) => {
    setState(prevState => {
      const columns = prevState.columns.map(column => ({
        ...column,
        width: params.colDef.field === column.field ? params.width : column.width
      }))

      return {
        ...prevState,
        columns,
        // Updating the column widths is required. If we don't update them we'll have an issue
        // that if we change the width and then request new data the width will revert to
        // the previous value.
        columnWidths: mapColumnsToColumnWidths(columns)
      }
    })
  }, [])

  const onColumnOrderChange = useCallback(({ oldIndex, targetIndex, column }: GridColumnOrderChangeParams) => {
    setState(prevState => {
      // Need to create a copy first since we don't
      // want to mutate the original array.
      const columns = prevState.columns.slice()
      // Remove column from old index in the array.
      columns.splice(oldIndex, 1)
      // Insert column into target index in the array.
      columns.splice(targetIndex, 0, column)

      return {
        ...prevState,
        // Sorting the visibility model is required since we're using it to also render
        // the columns in the proper order. Without sorting it here columns will
        // go back to their previous position if we change sorting or filtering after changing the order.
        columnVisibilityModel: sortColumnVisibilityModelByColumnOrder(prevState.columnVisibilityModel, columns),
        columns
      }
    })
  }, [])

  const settings = useQuery(
    [key, 'settings'],
    async () => {
      const settings = await loadSettings()

      return settings.Data.Columns
    },
    {
      onSuccess: columns => {
        setState(prevState => ({
          ...prevState,
          settingsInitialized: true,
          columnVisibilityModel: mapListColumnSettingsToColumnVisibilityModel(columns),
          sortModel: mapListSettingsToSortModel(columns),
          pinnedColumns: mapListSettingsToPinnedColumns(columns),
          columnWidths: mapListSettingsToColumnWidths(columns)
        }))
      },
      refetchOnWindowFocus: false
    }
  )

  const fields = useMemo(() => {
    return mapVisibilityModelToListColumns(state.columnVisibilityModel)
  }, [state.columnVisibilityModel])

  const sorting = useMemo(() => {
    return mapSortModelToListOrderBy(state.sortModel)
  }, [state.sortModel])

  const filter = useMemo(() => {
    return mapGridFilterModelToListSearch(state.filterModel, isSupplierOrderEnabled)
  }, [state.filterModel, isSupplierOrderEnabled])

  const rows = useQuery(
    [key, 'rows', fields, sorting, filter, state.paginationModel.pageSize, state.paginationModel.page],
    async () => {
      const result = await loadData({
        fields,
        page: state.paginationModel.page,
        perPage: state.paginationModel.pageSize,
        orderBy: sorting,
        search: filter
      })

      return result
    },
    {
      enabled: state.settingsInitialized,
      onSuccess: ({ Data, Structure, Paging }) => {
        const lookupTable = mapStructureToColumnTypeLookupTable(Structure)

        setState(prevState => {
          const columnVisibilityModel = combineColumnVisibilityModelAndStructure(prevState.columnVisibilityModel, Structure)

          const columns: GridColDef<T>[] = Object.keys(columnVisibilityModel).map(field => {
            const colDef = queryColumnDefinitionByColumnField(lookupTable, field)
            const [entityName, columnName] = tl(field)
            const width = queryColumnWidth(prevState.columnWidths, field)

            // Check if this is a VIN column
            const isVinColumn = field.toLowerCase().includes('vin')
            return {
              field,
              headerName: `${entityName} - ${columnName}`,
              width,
              // We need the combination of getOr and ?? ''.
              // Without it we can return null or undefined as a value
              // which will cause the grid to crash.
              valueGetter: params => getOr('', field, params.row) ?? '',
              renderHeader: ({ colDef }) => <DataGridHeader field={colDef.field} />,
              // Add custom renderCell for VIN columns if useVinUnmask is provided
              ...(isVinColumn && useVinUnmask
                ? {
                    renderCell: (params: any) => {
                      const vin = getOr('', field, params.row) ?? ''
                      const row = params.row as any
                      const vehicleUUID =
                        row?.Vehicle?.UUID || row?.Vehicle?.Uuid || row?.Vehicle?.uuid || getOr('', 'Vehicle.UUID', row) || ''
                      const registration =
                        getOr('', 'Vehicle.Registration', row) ||
                        getOr('', 'registration', row) ||
                        getOr('', 'ArchiveRow.Registration', row) ||
                        ''
                      const vehicleIdentifier = vehicleUUID || registration
                      const workorderUUID = getOr('', 'Workorder.UUID', row) || undefined
                      const updatedAt = getOr('', 'updatedAt', row) || undefined
                      const resourceId =
                        findKeyInObject(row, 'ArchiveRow.InvoiceUUID') ||
                        findKeyInObject(row, 'Invoice.WorkorderInvoiceUUID') ||
                        findKeyInObject(row, 'Invoice.InventoryVehicleTransactionInvoiceUUID') ||
                        findKeyInObject(row, 'Quotation.WorkorderQuotationUUID') ||
                        undefined
                      return vin && useVinUnmask ? (
                        <MaskedComponent
                          vin={vin}
                          vehicleUUID={vehicleIdentifier}
                          workorderUUID={workorderUUID}
                          updatedAt={updatedAt}
                          useVinUnmask={useVinUnmask}
                          registration={registration}
                          resourceID={resourceId}
                        />
                      ) : (
                        vin
                      )
                    }
                  }
                : {}),
              // Add custom renderCell for VIN columns if useVinUnmask is provided
              ...(isVinColumn && useVinUnmask
                ? {
                    renderCell: (params: any) => {
                      const vin = getOr('', field, params.row) ?? ''
                      const row = params.row as any
                      const vehicleUUID =
                        row?.Vehicle?.UUID || row?.Vehicle?.Uuid || row?.Vehicle?.uuid || getOr('', 'Vehicle.UUID', row) || ''
                      const registration =
                        getOr('', 'Vehicle.Registration', row) ||
                        getOr('', 'registration', row) ||
                        getOr('', 'ArchiveRow.Registration', row) ||
                        ''
                      const vehicleIdentifier = vehicleUUID || registration
                      const workorderUUID = getOr('', 'Workorder.UUID', row) || undefined
                      const updatedAt = getOr('', 'updatedAt', row) || undefined

                      return vin && useVinUnmask ? (
                        <MaskedComponent
                          vin={vin}
                          vehicleUUID={vehicleIdentifier}
                          workorderUUID={workorderUUID}
                          updatedAt={updatedAt}
                          useVinUnmask={useVinUnmask}
                        />
                      ) : (
                        vin
                      )
                    }
                  }
                : {}),
              ...colDef
            }
          })

          return {
            ...prevState,
            rows: Data,
            columns,
            columnVisibilityModel,
            rowCount: Paging.Total
          }
        })
      },
      refetchOnWindowFocus: false
    }
  )

  const handleRestoreSettings = useCallback(async (): Promise<void> => {
    if (!restoreSettings || !settings || !rows) return
    try {
      await restoreSettings()
      await settings.refetch()
      await rows.refetch()
    } catch (error) {
      throw error
    }
  }, [restoreSettings, settings, rows])

  const apiRef = useGridApiRef()

  useEffect(() => {
    apiRef.current.restoreSettings = handleRestoreSettings
  }, [apiRef, handleRestoreSettings])

  useSyncListSettings({
    columns: state.columns,
    columnVisibilityModel: state.columnVisibilityModel,
    pinnedColumns: state.pinnedColumns,
    sortModel: state.sortModel,
    hasAllColumnsMadeVisible: state.hasAllColumnsMadeVisible,
    saveSettings
  })

  // If we set the passed value before the column definitions it might reset that value to it's default value.
  // This is due to how the MUI DataGrid works internally.
  function resolveValueAfterColumnsAreDefined<T>(model: T): T | undefined {
    return !state.columns.length ? undefined : model
  }

  return {
    columns: state.columns,
    columnVisibilityModel: state.columnVisibilityModel,
    filterMode: 'server',
    filterModel: resolveValueAfterColumnsAreDefined(state.filterModel),
    getRowId,
    loading: settings.isInitialLoading || settings.isFetching || rows.isInitialLoading || rows.isFetching,
    onColumnOrderChange,
    onColumnVisibilityModelChange,
    onColumnWidthChange,
    onFilterModelChange,
    onPaginationModelChange,
    onPinnedColumnsChange,
    onSortModelChange,
    pageSizeOptions: [25, 50, 100],
    pagination: true,
    paginationMode: 'server',
    paginationModel: state.paginationModel,
    pinnedColumns: state.pinnedColumns,
    rowCount: state.rowCount,
    rows: state.rows,
    apiRef,
    slotProps: {
      exportDialog: {
        exportCSV: columns => exportCSV({ fields: columns.join(','), orderBy: sorting, search: filter }),
        exportExcel: columns => exportExcel({ fields: columns.join(','), orderBy: sorting, search: filter }),
        filename: key
      }
    },
    sortingMode: 'server',
    sortModel: resolveValueAfterColumnsAreDefined(state.sortModel)
  }
}

interface DataGridProps<T extends GridValidRowModel> extends DataGridProProps<T> {
  hideToolbarSeparator?: boolean
}

export function DataGrid<T extends GridValidRowModel>(props: DataGridProps<T>) {
  const { hideToolbarSeparator, ...dataGridProps } = props
  const localeText = useDataGridLocaleText()

  const [height, autoHeightOffsetRef] = useAutoHeight<HTMLDivElement>()

  return (
    <Box
      ref={autoHeightOffsetRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height,
        minHeight: 600,
        position: 'relative',
        ...(!hideToolbarSeparator && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 54, // Standard DataGrid header height
            left: 0,
            right: 0,
            width: '100%',
            height: '2px',
            backgroundColor: '#d3d3d3',
            pointerEvents: 'none',
            zIndex: 10
          }
        })
      }}
    >
      <DataGridPro
        localeText={localeText}
        hideFooterSelectedRowCount
        {...dataGridProps}
        slots={{ addButton: DataGridToolbarAddButton, toolbar: DataGridToolbar, ...dataGridProps.slots }}
        sx={{
          backgroundColor: theme => theme.palette.background.default,
          boxShadow: theme => theme.shadows[1],
          border: 0,
          ...dataGridProps.sx
        }}
        slotProps={{
          ...dataGridProps.slotProps,
          footer: {
            ...dataGridProps.slotProps?.footer,
            sx: {
              [`.${tablePaginationClasses.selectLabel}`]: { marginBottom: 0 },
              [`.${tablePaginationClasses.displayedRows}`]: { marginBottom: 0 },
              ...dataGridProps.slotProps?.footer?.sx
            }
          }
        }}
        // Disable the filter option in the column menu.
        // This isn't needed since we use header filters.
        disableColumnFilter
        // Needed to be able to test the grid properly.
        // Otherwise we won't see all columns
        disableVirtualization
        unstable_headerFilters
      />
    </Box>
  )
}
